import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, Textarea } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { PortalUserDocument, RevokeDashboardDocument } from '../../graphql/__generated__/types';
import { dashboardRefusalOf } from './dashboardRefusal';
import { formatDate } from './display';

interface Props {
    /** The gesture's subject: the **membership**, never the person. */
    membershipId: string;
    organizationName: string;
    personName: string;
    entry: { dashboardId: string; name: string; grantedAt: string; grantedByName: string };
    onClose: () => void;
}

/**
 * Withdrawing a dashboard from **one membership**: PCG-106.
 *
 * **Attention, the line is deleted, where this repository revokes elsewhere, and the wording
 * has to say so.** A membership carries a *state*: a status a view derives, a closing
 * date and author the sheet reads back, a reopening gesture that needs the row, so it
 * is revoked and kept. A `dashboard_membership` carries no state at all: no status, no
 * revocation column, no view over it, and **its presence is the fact**. The precedent
 * it resembles is `disableAccountModule`, not `revokeMembership`.
 *
 * Two consequences this dialog must not blur:
 *
 * - **there is nothing to reopen.** Granting it again later is not a reopening, it is
 *   the first gesture over again, under the same conditions. Offering « rouvrir »
 *   would promise a state the row does not keep;
 * - **what the deletion destroys goes into the journal as it destroys it**: since
 *   when the habilitation existed, and who granted it. After this, « qui l'avait » is
 *   answered by `activity_log` and by nothing on the sheet. So both facts are printed
 *   here, before the click, and the confirmation says where they went.
 *
 * The motive is required on screen although the API takes it as optional: no column
 * carries it (the row disappears) so the journal is the only place it could live.
 */
const RevokeDashboardDialog = ({ membershipId, organizationName, personName, entry, onClose }: Props) => {
    const [reason, setReason] = useState('');

    const [revoke, { data, loading, error }] = useMutation(RevokeDashboardDocument, {
        // The sheet lists the habilitations of every membership, and this deletes one.
        // `GrantedDashboard` carries no `id`, so nothing here is normalised and the
        // payload cannot be written into the sheet's cache entry.
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const submit = () => {
        // The refusal is read off `error` below; without this every refusal also
        // reaches the console as an unhandled rejection.
        revoke({ variables: { dashboardId: entry.dashboardId, membershipId, reason: reason.trim() } }).catch(() => {});
    };

    // Computed before the templates below: `ttag` refuses a call expression inside a
    // localized string, and the extraction fails rather than shipping English.
    const grantedOn = formatDate(entry.grantedAt);
    const grantedBy = entry.grantedByName;

    const done = data?.revokeDashboard;
    // What the membership holds afterwards, read from the payload rather than counted
    // on a list that has just been refetched under the dialog.
    const remaining = done?.length ?? 0;
    const blocked = reason.trim().length === 0;

    return (
        <Modal open onClose={onClose} title={c('Revoke dashboard').t`Withdraw this dashboard`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{entry.name}</b>
                        <span className="text-text-600">
                            {' '}
                            · {personName} · {organizationName}
                        </span>
                    </p>

                    {done ? (
                        <Alert type="success" title={c('Revoke dashboard').t`The dashboard is withdrawn.`}>
                            <p className="text-sm">
                                {c('Revoke dashboard')
                                    .t`The line is deleted, so this sheet keeps nothing of it: no closing date, no author, nothing to reopen. What it held — granted on ${grantedOn} by ${grantedBy} — went into the activity journal as the deletion destroyed it, and that is where « who had it » is answered from now on.`}
                            </p>
                            <p className="mt-2 text-sm">
                                {remaining === 0
                                    ? c('Revoke dashboard')
                                          .t`This person now holds no dashboard on this account. Granting one again later is not a reopening: it is the same gesture as the first grant, under the same conditions.`
                                    : ngettext(
                                          msgid`This person still holds ${remaining} dashboard on this account.`,
                                          `This person still holds ${remaining} dashboards on this account.`,
                                          remaining
                                      )}
                            </p>
                        </Alert>
                    ) : (
                        <>
                            {/* Before the button, never after: a warning read on the
                                confirmation comes too late to be one. */}
                            <Alert type="warning">
                                {c('Revoke dashboard')
                                    .t`Withdrawing deletes the line rather than closing it. An habilitation carries no status and no closing date — its presence is the fact — so nothing here will record that it existed, and there will be nothing to reopen: granting it again later is the first gesture over again. This person stops seeing this dashboard at once.`}
                            </Alert>

                            {/* The two facts the deletion destroys, printed while they
                                still exist. After the click they live in the journal
                                only, and the sheet can no longer show them. */}
                            <p className="text-xs text-text-600">
                                {c('Revoke dashboard')
                                    .t`Granted on ${grantedOn} by ${grantedBy}. The deletion carries both into the activity journal, which is the only place they survive.`}
                            </p>

                            <div>
                                <label
                                    htmlFor="revoke-dashboard-reason"
                                    className="mb-1.5 block text-sm font-medium text-text-900"
                                >
                                    {c('Revoke dashboard').t`Reason for withdrawing`}
                                </label>
                                <Textarea
                                    id="revoke-dashboard-reason"
                                    value={reason}
                                    onValue={setReason}
                                    placeholder={c('Revoke dashboard')
                                        .t`Granted by mistake, change of role, reporting discontinued…`}
                                />
                                <p className="mt-1.5 text-xs text-text-600">
                                    {c('Revoke dashboard')
                                        .t`No column carries it — the line disappears — so it is written to the journal with the gesture, and it is the only trace of why.`}
                                </p>
                            </div>

                            {error && <RefusalNotice refusal={dashboardRefusalOf(error, 'revoke')} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button category="destructive" loading={loading} disabled={blocked} onClick={submit}>
                            {c('Revoke dashboard').t`Withdraw`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default RevokeDashboardDialog;
